import { useState } from 'react';
import { useI18n } from '../hooks/use-i18n';
import PortraitLayers from '../components/portrait-layers';

const CATEGORY_COLORS = {
  work: '#c8a96e', goals: '#c8a96e', mission: '#c8a96e',
  patterns: '#7a9e82', preferences: '#7a9e82',
  life: '#9b8fc4', cultural: '#9b8fc4', stakes: '#9b8fc4',
  professional_vertical: '#c8a96e', skill_dna: '#7a9e82',
};

const CATEGORY_LABELS = {
  work: 'Work & Business', goals: 'Goals & Direction', patterns: 'How You Think & Operate',
  life: 'Personal & Life', preferences: 'Communication Style',
  professional_vertical: 'Professional Domain', skill_dna: 'Capabilities',
  cultural: 'Cultural Context', mission: 'Long-Term Mission', stakes: 'Stakes',
};

const SYNTHESIS_CATEGORIES = [
  'work', 'goals', 'patterns', 'life', 'preferences',
  'professional_vertical', 'skill_dna', 'cultural', 'mission', 'stakes',
];

export default function ProfileFeature({ userId, supabase, userRecord, session, onClose }) {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState('portrait');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [feedbackState, setFeedbackState] = useState({});
  const [correctionInputs, setCorrectionInputs] = useState({});
  const [synthesizing, setSynthesizing] = useState(false);
  const [synthError, setSynthError] = useState('');

  const portrait = userRecord?.portrait_v2 || {};
  const syntheses = portrait.syntheses || {};
  const hasSyntheses = Object.keys(syntheses).length > 0;
  const initial = (userRecord?.first_name || 'U')[0].toUpperCase();
  const name = userRecord?.first_name
    ? `${userRecord.first_name} ${userRecord.last_name || ''}`.trim()
    : 'User';

  async function handleGenerateInsights() {
    setSynthesizing(true);
    setSynthError('');
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const res = await fetch('/api/portrait/synthesize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authSession?.access_token || ''}` },
      });
      if (res.ok) {
        const { data: fresh } = await supabase.from('users').select('portrait_v2').eq('id', userId).single();
        if (fresh?.portrait_v2) {
          // Force re-render with updated portrait
          window.location.reload();
        }
      } else {
        setSynthError('Could not generate — try again');
      }
    } catch (e) {
      setSynthError('Could not generate — try again');
    }
    setSynthesizing(false);
  }

  async function handleConfirm(cat) {
    setFeedbackState(prev => ({ ...prev, [cat]: 'confirmed' }));
    try {
      const p = { ...portrait };
      if (p.syntheses?.[cat]) p.syntheses[cat].confirmed = true;
      await supabase.from('users').update({ portrait_v2: p }).eq('id', userId);
    } catch (e) { /* non-blocking */ }
  }

  async function handleCorrection(cat, type) {
    const text = correctionInputs[cat];
    if (!text?.trim()) return;
    setFeedbackState(prev => ({ ...prev, [cat]: 'corrected' }));
    console.log('portrait correction:', { userId, category: cat, correction: text, type, original: syntheses[cat]?.synthesis });
    setCorrectionInputs(prev => ({ ...prev, [cat]: '' }));
  }

  function toggleExpand(cat) {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  }

  const removePortraitFact = async (category, fact) => {
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authSession?.access_token || ''}` },
        body: JSON.stringify({ action: 'removePortraitFact', userId, category, fact }),
      });
    } catch { /* non-fatal — fact removal */ }
  };

  // Progress tab data
  const progressData = null; // will be loaded when tab activates
  const sessions = [];
  const totalSC = userRecord?.session_count || 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: 'Georgia, serif', background: '#07070a' }}>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 28px', borderBottom: '0.5px solid #1e1e2a', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: 'rgba(200,169,110,0.15)', border: '1px solid #c8a96e33',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#c8a96e', fontSize: 17, fontWeight: 'bold',
          }}>
            {initial}
          </div>
          <div>
            <div style={{ color: '#c8a96e', fontSize: 20, fontWeight: 'normal' }}>{name}</div>
            <div style={{ color: '#555', fontSize: 13 }}>
              {userRecord?.username ? `@${userRecord.username}` : ''}{userRecord?.username ? ' · ' : ''}{userRecord?.plan || 'Free plan'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            background: '#111', border: '1px solid #272736', borderRadius: 6,
            padding: '4px 12px', color: '#555', fontSize: 12,
          }}>
            {totalSC} session{totalSC !== 1 ? 's' : ''}
          </div>
          <div
            onClick={onClose}
            style={{ cursor: 'pointer', color: '#333', display: 'flex', alignItems: 'center' }}
            onMouseEnter={e => e.currentTarget.style.color = '#888'}
            onMouseLeave={e => e.currentTarget.style.color = '#333'}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          </div>
        </div>
      </div>

      {/* Tab nav */}
      <div style={{
        display: 'flex', justifyContent: 'center', gap: 36,
        padding: '0', borderBottom: '0.5px solid #1e1e2a', flexShrink: 0,
      }}>
        {['portrait', 'progress', 'protocols'].map(tab => (
          <div
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '12px 4px', cursor: 'pointer',
              color: activeTab === tab ? '#c8a96e' : '#555',
              borderBottom: `1.5px solid ${activeTab === tab ? '#c8a96e' : 'transparent'}`,
              fontSize: 14, textTransform: 'capitalize',
            }}
            onMouseEnter={e => { if (activeTab !== tab) e.currentTarget.style.color = '#888'; }}
            onMouseLeave={e => { if (activeTab !== tab) e.currentTarget.style.color = '#555'; }}
          >
            {tab}
          </div>
        ))}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px' }}>

        {/* ═══ PORTRAIT TAB ═══ */}
        {activeTab === 'portrait' && (
          <div>
            {hasSyntheses ? (
              <PortraitLayers
                portrait={portrait}
                syntheses={syntheses}
                inferredPatterns={portrait.inferred_patterns}
                onFeedback={async (key, value) => {
                  if (value === 'right') handleConfirm(key);
                  else if (value === 'partial') setFeedbackState(p => ({ ...p, [key]: 'partial' }));
                  else if (value === 'wrong') setFeedbackState(p => ({ ...p, [key]: 'wrong' }));
                }}
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '60px 0' }}>
                <div style={{ color: '#555', fontSize: 13, fontStyle: 'italic', fontFamily: 'Georgia, serif', textAlign: 'center', lineHeight: 1.8 }}>
                  {userRecord?.ai_name || 'Shift'} is still learning about you.<br />
                  <span style={{ opacity: 0.5, fontSize: 12 }}>Keep having conversations — your portrait builds over time.</span>
                </div>
                <button
                  onClick={handleGenerateInsights}
                  disabled={synthesizing}
                  style={{
                    fontSize: 12, color: synthesizing ? '#555' : '#c8a96e',
                    background: 'rgba(200,169,110,0.08)', border: '1px solid rgba(200,169,110,0.2)',
                    borderRadius: 5, padding: '7px 16px', cursor: synthesizing ? 'default' : 'pointer',
                    fontFamily: 'Georgia, serif',
                  }}
                >
                  {synthesizing ? 'Generating...' : 'Generate insights'}
                </button>
                {synthError && <div style={{ color: '#c46050', fontSize: 11, fontStyle: 'italic', marginTop: 8 }}>{synthError}</div>}
              </div>
            )}
          </div>
        )}

        {/* ═══ PROGRESS TAB ═══ */}
        {activeTab === 'progress' && (
          <div style={{ color: '#555', fontSize: 13, fontStyle: 'italic', textAlign: 'center', padding: '60px 0' }}>
            Progress dashboard loading...
          </div>
        )}

        {/* ═══ PROTOCOLS TAB ═══ */}
        {activeTab === 'protocols' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div style={{ color: '#888', fontSize: 14, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>Your Protocols</div>
              <div style={{ color: '#555', fontSize: 14, lineHeight: 1.6 }}>
                Behavioral contracts between you and {userRecord?.ai_name || 'Shift'}. These build automatically as you work together. You confirm each one before it locks.
              </div>
            </div>
            {(userRecord?.portrait_v2?.protocols?.current || []).length === 0 ? (
              <div style={{ color: '#333', fontSize: 15, fontStyle: 'italic', lineHeight: 1.7, paddingTop: 8 }}>
                No protocols yet — they build as we work together.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(userRecord?.portrait_v2?.protocols?.current || []).map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', background: '#07070a', border: '1px solid #222', borderRadius: 6 }}>
                    <div style={{ flex: 1, color: '#aaa', fontSize: 15, lineHeight: 1.6 }}>{typeof p === 'string' ? p : (p?.text || p?.fact || JSON.stringify(p))}</div>
                    <button style={{ background: 'none', border: 'none', color: '#8b4a4a', fontSize: 13, cursor: 'pointer', flexShrink: 0, letterSpacing: '0.06em' }}>
                      remove
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={() => console.log('add protocol')}
              style={{ alignSelf: 'flex-start', fontSize: 12, color: '#555', border: '1px solid #2a2a2a', borderRadius: 5, padding: '7px 14px', background: 'none', cursor: 'pointer', fontFamily: 'Georgia, serif' }}
            >
              + Add protocol
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
